import { FC, useState } from 'react';
import { Card, Progress, Spin, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { Trans } from 'react-i18next';

import { CountryFlag, Popover } from 'componets/ui';

import { Project, RowPropsT } from 'models';

import { useTranslate } from 'hooks';

import Utils from 'utils/utils';

import camera from 'assets/icons/camera.svg';

export const PrimaryMarketItem: FC<{ item: Project }> = ({ item }) => {
	const {
		filledText,
		interestRateAnnualText,
		projectAmountText,
		investmentPeriodText,
		viewProjectText,
		riskLevelText,
	} = useTranslate([
		'filledText',
		'investmentPeriodText',
		'projectAmountText',
		'interestRateAnnualText',
		'viewProjectText',
		'riskLevelText',
	]);

	const [loading, setLoading] = useState(true);

	const percentValue = (+item.investedAmount / +item.assignedAmount) * 100;
	const percent = percentValue === 0 ? 0 : percentValue.toFixed(2);

	return (
		<div className="primaryMarket__itemWrapper">
			<Card className="primaryMarket__item" bordered={false}>
				<Link to={`/account/projects/${item.loanId}`} className="primaryMarket__linkWrapper">
					<div className="primaryMarket__photo">
						<div className="primaryMarket__row">
							{item.riskLevel && (
								<div className="primaryMarket__risk">
									<Popover
										content={riskLevelText}
										trigger="hover"
										className="primaryMarket__popover"
										placement="bottom"
									>
										<div className="primaryMarket__rickContent">{item.riskLevel}</div>
									</Popover>
								</div>
							)}
							<CountryFlag country={item.country.iso} />
						</div>

						{loading && (
							<div className="primaryMarket__spinner">
								<Spin size="large" />
							</div>
						)}

						<img
							onLoad={() => {
								setLoading(false);
							}}
							src={item.mainPhoto}
							alt={item.title}
							onError={({ currentTarget }) => {
								currentTarget.onerror = null;
								currentTarget.className = 'error';
								currentTarget.src = camera;
							}}
						/>
					</div>
					<div className="primaryMarket__info">
						<div className="primaryMarket__block">
							<Typography.Title level={5}>{item.title}</Typography.Title>

							<div className="primaryMarket__progress">
								<Progress showInfo={false} percent={+percent} strokeColor="#65ABFF" />
								<div className="primaryMarket__row">
									<Typography.Text type="secondary">
										{percent}% {filledText}
									</Typography.Text>
									<Typography.Text type="secondary">
										<Trans
											i18nKey="leftText"
											values={{ money: Utils.moneyFormat(+item.availableToInvest) }}
										/>
									</Typography.Text>
								</div>
							</div>
						</div>

						<div className="primaryMarket__line" />
						<div className="primaryMarket__block">
							<PrimaryMarketRow title={interestRateAnnualText} value={item.interestRate} />

							<PrimaryMarketRow title="LTV" value={item.ltvRate} />

							<PrimaryMarketRow
								title={projectAmountText}
								value={Utils.moneyFormat(+item.assignedAmount)}
							/>

							<PrimaryMarketRow title={investmentPeriodText} value={item.investmentPeriod} />
						</div>

						<div className="primaryMarket__button">
							<div className="primaryMarket__link">{viewProjectText}</div>
						</div>
					</div>
				</Link>
			</Card>
		</div>
	);
};

const PrimaryMarketRow: FC<RowPropsT> = ({ title, value }) => {
	return (
		<div className="primaryMarket__row">
			<Typography.Text className="primaryMarket__rowTitle" type="secondary">
				{title}
			</Typography.Text>
			<Typography.Text className="primaryMarket__infoText">{value}</Typography.Text>
		</div>
	);
};
